import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import InvButton from './InvButton';
import { formatINR, formatDate, formatDateTime, parseMoney, formatQty } from '../utils';
import { IPD_COLORS } from '../theme';

/**
 * Thermal-friendly receipt view (80mm). Never shows WAC.
 * Shows per-line discounts and whole-bill discount distinctly.
 */
export default function ReceiptView({
  receiptNumber,
  billNumber,
  patientLabel = 'Walk-in',
  doctorName,
  lines = [],
  amountPayable,
  /** Whole-bill discount (not line item discounts). */
  billDiscount = 0,
  /** @deprecated use billDiscount — kept for older call sites */
  discount = 0,
  /** Sum of line item discounts; if omitted, derived from lines. */
  lineDiscountTotal,
  /** Gross before discounts; if omitted, derived from lines. */
  totalBasic,
  tax = 0,
  modes = [],
  cashierName,
  createdAt,
  orgName = 'HlthTek Pharmacy',
  orgSubtitle = 'Tax Invoice / Receipt',
}) {
  function handlePrint() {
    window.print();
  }

  const normalized = (lines || []).map((line) => normalizeReceiptLine(line));
  const derivedBasic = normalized.reduce((s, l) => s + l.basic, 0);
  const derivedLineDisc = normalized.reduce((s, l) => s + l.itemDiscount, 0);
  const derivedBillShare = normalized.reduce((s, l) => s + l.billShare, 0);

  const subtotal = totalBasic != null && totalBasic !== ''
    ? parseMoney(totalBasic)
    : derivedBasic;
  const lineDisc = lineDiscountTotal != null && lineDiscountTotal !== ''
    ? parseMoney(lineDiscountTotal)
    : derivedLineDisc;
  const wholeBillDisc = parseMoney(billDiscount) || parseMoney(discount) || derivedBillShare;
  const taxAmt = parseMoney(tax);
  const payable = amountPayable != null && amountPayable !== ''
    ? parseMoney(amountPayable)
    : Math.max(0, subtotal - lineDisc - wholeBillDisc + taxAmt);

  return (
    <Box>
      <Box
        className="pharmacy-receipt"
        sx={{
          maxWidth: 360,
          mx: 'auto',
          overflow: 'hidden',
          borderRadius: '12px',
          border: `1px solid ${IPD_COLORS.border}`,
          backgroundColor: '#fff',
          boxShadow: IPD_COLORS.shadow.card,
          '@media print': {
            border: 'none',
            boxShadow: 'none',
            borderRadius: 0,
            maxWidth: '80mm',
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            background: `linear-gradient(135deg, ${IPD_COLORS.primaryDark} 0%, ${IPD_COLORS.primary} 55%, ${IPD_COLORS.primaryLight} 100%)`,
            color: '#fff',
            textAlign: 'center',
            '@media print': {
              background: IPD_COLORS.primary,
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
            },
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 16, letterSpacing: 0.4 }}>
            {orgName}
          </Typography>
          <Typography sx={{ fontSize: 11, opacity: 0.85, mt: 0.35 }}>
            {orgSubtitle}
          </Typography>
        </Box>

        <Box sx={{ px: 2.5, py: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
              mb: 1.5,
              p: 1.25,
              borderRadius: '8px',
              backgroundColor: IPD_COLORS.panelHeaderBg,
              border: `1px solid ${IPD_COLORS.border}`,
            }}
          >
            <MetaItem label="Receipt" value={receiptNumber || '—'} />
            <MetaItem label="Bill" value={billNumber || '—'} />
            <MetaItem label="Patient" value={patientLabel || 'Walk-in'} />
            <MetaItem label="Date" value={formatDateTime(createdAt || new Date())} />
            {doctorName ? (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <MetaItem label="Doctor" value={doctorName} />
              </Box>
            ) : null}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 44px 68px',
              gap: 0.5,
              px: 0.5,
              mb: 0.75,
            }}
          >
            <Typography sx={colHeadSx}>Item</Typography>
            <Typography sx={{ ...colHeadSx, textAlign: 'right' }}>Qty</Typography>
            <Typography sx={{ ...colHeadSx, textAlign: 'right' }}>Amount</Typography>
          </Box>
          <Divider sx={{ mb: 1, borderColor: IPD_COLORS.border }} />

          {normalized.map((line, i) => (
            <Box key={line.id || i} sx={{ mb: 1.35, px: 0.5 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 44px 68px',
                  gap: 0.5,
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.3, color: IPD_COLORS.textPrimary }}>
                    {line.name}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: IPD_COLORS.textSecondary, mt: 0.2, lineHeight: 1.4 }}>
                    {line.batch_number ? `Batch ${line.batch_number}` : 'Batch —'}
                    {line.expiry_date ? ` · Exp ${formatDate(line.expiry_date)}` : ''}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: IPD_COLORS.textMuted, mt: 0.15 }}>
                    {formatQty(line.qty)} × {formatINR(line.rate)}
                  </Typography>
                  {line.itemDiscount > 0 && (
                    <Typography sx={{ fontSize: 10.5, color: IPD_COLORS.success.text, mt: 0.2, fontWeight: 600 }}>
                      Discount − {formatINR(line.itemDiscount)}
                    </Typography>
                  )}
                  {line.billShare > 0 && (
                    <Typography sx={{ fontSize: 10.5, color: IPD_COLORS.textSecondary, mt: 0.15 }}>
                      Bill disc share − {formatINR(line.billShare)}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: 12, textAlign: 'right', fontWeight: 600, pt: 0.1 }}>
                  {formatQty(line.qty)}
                </Typography>
                <Box sx={{ textAlign: 'right', pt: 0.1 }}>
                  {line.itemDiscount > 0 || line.billShare > 0 ? (
                    <>
                      <Typography sx={{
                        fontSize: 10.5,
                        color: IPD_COLORS.textMuted,
                        textDecoration: 'line-through',
                        lineHeight: 1.2,
                      }}>
                        {formatINR(line.basic)}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.3 }}>
                        {formatINR(line.net)}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                      {formatINR(line.net || line.basic)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ))}

          <Divider sx={{ my: 1.25, borderColor: IPD_COLORS.border }} />

          <Box sx={{ px: 0.5 }}>
            <TotalRow label="Subtotal" value={formatINR(subtotal)} />
            {lineDisc > 0 && (
              <TotalRow label="Line discount" value={`− ${formatINR(lineDisc)}`} muted />
            )}
            {wholeBillDisc > 0 && (
              <TotalRow label="Bill discount" value={`− ${formatINR(wholeBillDisc)}`} muted />
            )}
            {(lineDisc > 0 || wholeBillDisc > 0) && (
              <TotalRow
                label="Total discount"
                value={`− ${formatINR(lineDisc + wholeBillDisc)}`}
                emphasis
              />
            )}
            {taxAmt > 0 && (
              <TotalRow label="Tax" value={formatINR(taxAmt)} />
            )}
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: `1.5px solid ${IPD_COLORS.primary}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: IPD_COLORS.primary }}>
                Amount payable
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: IPD_COLORS.primary }}>
                {formatINR(payable)}
              </Typography>
            </Box>
          </Box>

          {(modes || []).length > 0 && (
            <Box
              sx={{
                mt: 1.75,
                p: 1.25,
                borderRadius: '8px',
                backgroundColor: IPD_COLORS.panelHeaderBg,
                border: `1px solid ${IPD_COLORS.border}`,
              }}
            >
              <Typography sx={{
                fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5,
                textTransform: 'uppercase', color: IPD_COLORS.textSecondary, mb: 0.75,
              }}>
                Payment
              </Typography>
              {modes.map((m, i) => (
                <Typography
                  key={i}
                  sx={{
                    fontSize: 12,
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.35,
                    color: IPD_COLORS.textPrimary,
                  }}
                >
                  <span>{m.mode}{m.txn_ref ? ` · ${m.txn_ref}` : ''}</span>
                  <span style={{ fontWeight: 600 }}>{formatINR(m.amount)}</span>
                </Typography>
              ))}
            </Box>
          )}

          <Box sx={{ mt: 1.75, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
              Cashier: {cashierName || '—'}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                mt: 1,
                color: IPD_COLORS.primaryLight,
                letterSpacing: 0.3,
              }}
            >
              Thank you · Get well soon
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1, '@media print': { display: 'none' } }}>
        <InvButton variant="outlined" tone="teal" onClick={handlePrint}>Print receipt</InvButton>
      </Box>
    </Box>
  );
}

function normalizeReceiptLine(line) {
  const qty = parseMoney(line.qty ?? line.quantity);
  const rate = parseMoney(
    line.rate ?? line.unit_price ?? line.unit_rate ?? line.sale_price ?? line.mrp,
  );
  const basic = line.line_basic_amount != null && line.line_basic_amount !== ''
    ? parseMoney(line.line_basic_amount)
    : qty * rate;
  const itemDiscount = parseMoney(
    line.item_discount_amount
    ?? line.itemDiscount
    ?? line.discount
    ?? line.discount_amount
    ?? line.line_discount,
  );
  const billShare = parseMoney(
    line.bill_discount_share_amount ?? line.billShare ?? line.bill_discount_share,
  );
  const net = line.line_net_amount != null && line.line_net_amount !== ''
    ? parseMoney(line.line_net_amount)
    : line.total != null && line.total !== ''
      ? parseMoney(line.total)
      : Math.max(0, basic - itemDiscount - billShare);

  return {
    id: line.id,
    name: line.name || line.item_name || 'Item',
    batch_number: line.batch_number,
    expiry_date: line.expiry_date || line.expiry,
    qty,
    rate,
    basic,
    itemDiscount,
    billShare,
    net,
  };
}

function MetaItem({ label, value }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: IPD_COLORS.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: IPD_COLORS.textPrimary, mt: 0.15, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}

function TotalRow({ label, value, muted, emphasis }) {
  return (
    <Typography
      sx={{
        fontSize: emphasis ? 12.5 : 12,
        display: 'flex',
        justifyContent: 'space-between',
        mb: 0.4,
        fontWeight: emphasis ? 700 : 400,
        color: muted ? IPD_COLORS.textSecondary : IPD_COLORS.textPrimary,
      }}
    >
      <span>{label}</span>
      <span style={{ fontWeight: emphasis ? 700 : 600 }}>{value}</span>
    </Typography>
  );
}

const colHeadSx = {
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: IPD_COLORS.textMuted,
};
